import { type ReactNode, useState } from 'react'
import type { CoachingClient } from '../../../problems/types'
import {
  type OverviewData,
  useAdminOverview,
} from '../overview/admin-overview-provider'
import { ProfileEditorDialog } from './profile-editor-dialog'

/**
 * Client header (spec §6): the client name is always live (CoachingClient
 * prop); role / sprint / goal / targets come from the live engagement overview.
 * While loading or when no engagement resolves, the meta line renders an honest
 * placeholder instead of fabricated data — and the profile editor is withheld,
 * because without an engagement there is no id or version to write against.
 */
export function ClientHeader(props: { client: CoachingClient }): ReactNode {
  const { client } = props
  const { overview, isLoading } = useAdminOverview()
  const [editorOpen, setEditorOpen] = useState(false)

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[26px] font-bold text-(--cd-text)">
          {client.name}
        </h1>
        {overview ? (
          <button
            className="rounded-md bg-(--cd-card) px-3 py-1 text-xs font-medium text-(--cd-text-muted) hover:text-(--cd-text)"
            onClick={() => setEditorOpen(true)}
            type="button"
          >
            Edit profile
          </button>
        ) : null}
      </div>
      {isLoading ? (
        <p className="text-sm text-(--cd-text-faint)">
          Loading engagement overview…
        </p>
      ) : overview ? (
        <ClientHeaderMeta
          engagement={overview.engagement}
          targetCompanies={overview.targetCompanies}
        />
      ) : (
        <p className="text-sm text-(--cd-text-faint)">
          No engagement data available for this client yet.
        </p>
      )}
      {/* Gated on `editorOpen`, not just `overview`: Radix only unmounts
          `DialogContent` on close, not the view/container React tree, so a
          dialog that stayed mounted across a Cancel would keep its in-progress
          `values`/`conflictMessage` state and show them again on reopen. */}
      {overview && editorOpen ? (
        <ProfileEditorDialog
          engagement={{
            id: overview.engagement.id,
            clientId: overview.engagement.clientId,
            version: overview.engagement.version,
            goal: overview.engagement.goal,
            targetDate: overview.engagement.targetDate,
            clientElo: overview.engagement.clientElo,
            optimalEloBand: overview.engagement.optimalEloBand,
            targetRuns: overview.engagement.targetRuns,
            targetSubmits: overview.engagement.targetSubmits,
          }}
          onOpenChange={setEditorOpen}
          open={editorOpen}
        />
      ) : null}
    </div>
  )
}

function ClientHeaderMeta(props: {
  engagement: OverviewData['engagement']
  targetCompanies: OverviewData['targetCompanies']
}): ReactNode {
  const { engagement, targetCompanies } = props
  const role = [engagement.seniorityBandName, engagement.roleTrackName]
    .filter((part) => part && part.trim().length > 0)
    .join(' ')
  const targets = targetCompanies
    .map((company) => company.companyName)
    .join(', ')

  return (
    <>
      <p className="text-sm text-(--cd-text-muted)">
        {role || '—'} · Sprint {engagement.currentSprint} of{' '}
        {engagement.totalSprints} · Targets: {targets || '—'}
      </p>
      <p className="text-sm text-(--cd-text-muted)">
        Goal: {engagement.goal || '—'}
      </p>
    </>
  )
}
